import { createSlice } from "@reduxjs/toolkit";
import LocalStorageService from "../../common/services/LocalStorageService";
import {
  getItems,
  getOrder,
  getRoomOrders,
  refreshOrderInList,
} from "../orders/ordersActions";
import {
  getOrderMessages,
  markOrderMessagesRead,
  sendOrderMessage,
} from "./orderMessagesActions";

// Unread is keyed by order and persisted, so a refresh doesn't lose it. Read
// state is per organization by design — see the WeedVader chat guide — so
// nothing here is scoped to the logged-in user.
//
// The server's `unread_message_count` on the order list is authoritative when
// it is there: it is the only source that knows about messages that arrived
// while nobody had the app open. The socket keeps the count moving between
// refetches. Until that field ships, the socket count is all we have, which is
// why every read of it is guarded on the number actually being present.
const UNREAD_STORAGE_KEY = "orderChatUnread";

const readStoredUnread = () => {
  const stored = LocalStorageService?.get(UNREAD_STORAGE_KEY);
  return stored && typeof stored === "object" ? stored : {};
};

const persistUnread = (unread) => {
  LocalStorageService?.set(UNREAD_STORAGE_KEY, unread);
};

// Every order object carries `unread_message_count`, so every response that
// carries an order can correct the badge: the list, the detail, room orders,
// the socket's order frame and the route response. Orders without the field
// (an older backend) are left alone so the locally counted value stands in.
const applyServerUnread = (state, orders) => {
  const list = Array.isArray(orders) ? orders : [orders];
  let changed = false;
  list.forEach((order) => {
    const count = order?.unread_message_count;
    if (typeof count !== "number" || !order?.id) return;
    const key = String(order.id);
    if (count > 0) {
      if (state.unread[key] === count) return;
      state.unread[key] = count;
    } else {
      if (!(key in state.unread)) return;
      delete state.unread[key];
    }
    changed = true;
  });
  if (changed) persistUnread(state.unread);
};

const initialState = {
  list: {
    loading: false,
    error: null,
    items: [],
    orderId: null,
  },
  send: {
    loading: false,
    error: null,
  },
  unread: readStoredUnread(),
};

// A message can arrive twice — once as the POST response, once as the socket
// broadcast of the same row. `id` is ours and stable, so it is the dedupe key.
const appendMessage = (items, message) =>
  items?.some((item) => item?.id === message?.id)
    ? items
    : [...items, message];

const orderMessagesSlice = createSlice({
  name: "orderMessages",
  initialState,
  reducers: {
    addSocketOrderMessage: (state, { payload }) => {
      if (!payload?.order_id) return;

      if (state.list.orderId === payload.order_id) {
        state.list.items = appendMessage(state.list.items, payload);
      }

      // Only the customer's side counts as unread; our own outbound message
      // comes back over the same socket.
      if (payload?.direction === "INBOUND") {
        const key = String(payload.order_id);
        state.unread[key] = (state.unread[key] || 0) + 1;
        persistUnread(state.unread);
      }
    },
    markOrderChatRead: (state, { payload }) => {
      const key = String(payload);
      if (!state.unread[key]) return;
      delete state.unread[key];
      persistUnread(state.unread);
    },
    // For orders that reach us outside a thunk — the socket's `order` frame and
    // the route response — so a live row update corrects the badge too.
    syncOrderUnread: (state, { payload }) => applyServerUnread(state, payload),
    // Another tab in the shop opened the thread. Read is per organization, so
    // this badge goes too, without waiting for the next list refetch.
    setOrderChatUnread: (state, { payload }) => {
      const key = String(payload?.order_id);
      const count = Number(payload?.unread_count);
      if (!payload?.order_id || Number.isNaN(count)) return;
      if (count > 0) state.unread[key] = count;
      else delete state.unread[key];
      persistUnread(state.unread);
    },
    resetOrderMessages: (state) => {
      state.list = { ...initialState.list, items: [] };
      state.send = { loading: false, error: null };
    },
    resetOrderMessageSendError: (state) => {
      state.send.error = null;
    },
  },
  extraReducers: (builder) =>
    builder
      // The order list is what makes the badge survive a closed laptop: every
      // row carries its own count, so a pin knows it has unread the moment it
      // appears on the map rather than only after a socket frame arrives.
      .addCase(getItems.fulfilled, (state, { payload }) =>
        applyServerUnread(state, payload?.results)
      )
      .addCase(getRoomOrders.fulfilled, (state, { payload }) =>
        applyServerUnread(state, payload?.results)
      )
      .addCase(getOrder.fulfilled, (state, { payload }) =>
        applyServerUnread(state, payload)
      )
      .addCase(refreshOrderInList.fulfilled, (state, { payload }) =>
        applyServerUnread(state, payload)
      )
      // The read POST answers with what is still unread after it, which is the
      // authoritative number — trust it rather than assuming zero.
      .addCase(markOrderMessagesRead.fulfilled, (state, { payload }) =>
        applyServerUnread(state, {
          id: payload?.orderId,
          unread_message_count: payload?.unread_count,
        })
      )
      .addCase(getOrderMessages.pending, (state, { meta }) => {
        state.list.loading = true;
        state.list.error = null;
        state.list.orderId = meta?.arg ?? null;
      })
      .addCase(getOrderMessages.fulfilled, (state, { payload }) => {
        state.list.items = Array.isArray(payload) ? payload : [];
        state.list.loading = false;
      })
      .addCase(getOrderMessages.rejected, (state, { payload }) => {
        state.list.loading = false;
        state.list.error = payload;
      })
      .addCase(sendOrderMessage.pending, (state) => {
        state.send.loading = true;
        state.send.error = null;
      })
      .addCase(sendOrderMessage.fulfilled, (state, { payload }) => {
        state.send.loading = false;
        state.list.items = appendMessage(state.list.items, payload);
      })
      .addCase(sendOrderMessage.rejected, (state, { payload }) => {
        state.send.loading = false;
        state.send.error = payload;
      }),
});

export const {
  addSocketOrderMessage,
  markOrderChatRead,
  setOrderChatUnread,
  syncOrderUnread,
  resetOrderMessages,
  resetOrderMessageSendError,
} = orderMessagesSlice.actions;

export default orderMessagesSlice.reducer;
